import {
  renderReportHeatmapBundle,
  ReportLabelMode,
  ReportOverlayMode,
} from '@asset-viz/reporting/reportHeatmap'
import { Text, View } from '@react-pdf/renderer'
import {
  Alert,
  Button,
  Card,
  Image as AntImage,
  Input,
  message,
  Popconfirm,
  Select,
  Space,
  Typography,
} from 'antd'
import {
  CommercialFdeCdfSpecificationList,
  getCdfDisplayMetadata,
  useCdfDataFetcher,
  useInitializeDerivedCdfsFromRolodexData,
} from 'features/dataLayers'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { useGeckoLocale } from 'features/locale'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { ReportPage } from 'features/reporting/pdfGeneration/components/shared/ReportPage'
import { PdfProps, ReportSection } from 'features/reporting/types'
import sortBy from 'lodash/sortBy'
import { AddendumPages } from 'pages/reporting/reports/rug/components/AddendumPages'
import { ImagePageHeader } from 'pages/reporting/reports/rug/components/ImagePageHeader'
import { PdfHeatmapWithLegend } from 'pages/reporting/reports/rug/components/PdfHeatmapWithLegend'
import {
  LAYOUT,
  MATH_CONSTANTS,
  REPORT_COLORS,
  SPACING,
  TYPOGRAPHY,
} from 'pages/reporting/reports/rug/constants/rugReportStyles'
import { useSelectableComponentCDFs } from 'pages/reporting/reports/rug/hooks/useSelectableComponentCDFs'
import { RugReportSectionId } from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import { ComponentId, RugSectionStats } from 'pages/reporting/reports/rug/types'
import { getBoundsFromSectionable } from 'pages/reporting/reports/rug/utils/getBoundsFromSectionabe'
import {
  buildReportHeatmapBundlePaths,
  createReportHeatmapSectionKey,
  findSectionByKey,
  getBundleFilePaths,
  isFullComponentSectionKey,
  serializeReportHeatmapDescriptor,
} from 'pages/reporting/reports/rug/utils/reportHeatmapStorage'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoComponentDataFrame,
  GeckoComponentSpace,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  isInspectionThicknessCDF,
  Section,
} from 'types'

const { Text: AntText } = Typography

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
`

const ComponentSelector = styled.div`
  margin-bottom: 1rem;
`

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const ImageCard = styled(Card)`
  margin-bottom: 1rem;

  .ant-image {
    background-color: #ffffff;
  }
`

interface SectionScreenshot {
  componentId: string
  componentName: string
  sectionKey: string
  sectionName: string
  sortOrder: number
  signedUrl: string
  heatmapPath: string
  imagePath: string
  legendSignedUrl?: string
  legendPath?: string
  descriptorPath?: string
  bundlePath: string
}

const getSectionHeatmapCommentKey = (componentId: string, sectionKey: string) =>
  `heatmap:${componentId}:${sectionKey}`

const PDF_TEXT_WIDTH_PER_CHAR = 5.2
const PDF_CELL_HORIZONTAL_PADDING = SPACING.PADDING.EXTRA_SMALL * 2 + 8

type MeasurementColumnWidths = {
  label: number
  value: number
  locationX: number
  locationY: number
  total: number
}

const tableStyles = {
  container: {
    marginTop: SPACING.MARGIN.TINY,
    flexDirection: 'column' as const,
    gap: 0,
  },
  measurementsColumn: {
    flex: LAYOUT.FLEX.NARROW,
  },
  tableRow: {
    flexDirection: 'row' as const,
    borderBottom: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
  },
  tableCell: {
    padding: SPACING.PADDING.EXTRA_SMALL,
    borderRight: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
  },
  tableCellWide: {
    flex: LAYOUT.FLEX.WIDE,
  },
  tableCellNarrow: {
    flex: LAYOUT.FLEX.NARROW,
  },
  tableCellLast: {
    borderRight: 'none',
  },
  headerText: {
    fontSize: TYPOGRAPHY.FONT_SIZE.SMALL,
    fontWeight: TYPOGRAPHY.FONT_WEIGHT.BOLD,
    color: REPORT_COLORS.DARKER_GRAY,
  },
  cellText: {
    fontSize: TYPOGRAPHY.FONT_SIZE.SMALL,
  },
}

const pageStyles = {
  imagePage: {
    flexDirection: 'column' as const,
    padding: SPACING.PADDING.MEDIUM,
    paddingBottom: SPACING.PADDING.LARGE,
    fontSize: TYPOGRAPHY.FONT_SIZE.MEDIUM,
  },
}

function formatLocation(location?: string): string | undefined {
  return location?.replace(/″/g, '"')?.replace(/′/g, "'")
}

function clampNumber(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function estimatePdfColumnWidth(
  values: Array<string | undefined>,
  minWidth: number,
  maxWidth: number
) {
  const longestValueLength = Math.max(
    ...values.map((value) => value?.length ?? 0),
    0
  )

  return clampNumber(
    longestValueLength * PDF_TEXT_WIDTH_PER_CHAR + PDF_CELL_HORIZONTAL_PADDING,
    minWidth,
    maxWidth
  )
}

const TableRow: React.FC<{
  columnWidths: MeasurementColumnWidths
  label: string
  value: string | undefined
  locationX?: string
  locationY?: string
  isLastRow?: boolean
}> = ({
  columnWidths,
  label,
  value,
  locationX,
  locationY,
  isLastRow = false,
}) => (
  <View
    style={[tableStyles.tableRow, isLastRow ? { borderBottom: 'none' } : {}]}>
    <View style={[tableStyles.tableCell, { width: columnWidths.label }]}>
      <Text
        style={[
          tableStyles.cellText,
          { color: REPORT_COLORS.GECKO_BRIGHT_GREEN },
        ]}>
        {label}
      </Text>
    </View>
    <View style={[tableStyles.tableCell, { width: columnWidths.value }]}>
      <Text
        style={[
          tableStyles.cellText,
          { color: REPORT_COLORS.GECKO_BLUE_GREEN },
        ]}>
        {value}
      </Text>
    </View>
    <View style={[tableStyles.tableCell, { width: columnWidths.locationX }]}>
      <Text
        style={[
          tableStyles.cellText,
          { color: REPORT_COLORS.GECKO_BLUE_GREEN },
        ]}>
        {locationX}
      </Text>
    </View>
    <View
      style={[
        tableStyles.tableCell,
        { width: columnWidths.locationY },
        tableStyles.tableCellLast,
      ]}>
      <Text
        style={[
          tableStyles.cellText,
          { color: REPORT_COLORS.GECKO_BLUE_GREEN },
        ]}>
        {locationY}
      </Text>
    </View>
  </View>
)

const SectionStatsTable: React.FC<{
  section: Section
  sectionStats?: RugSectionStats
  nominalThickness?: GeckoMeasurement
  relevantCDF?: GeckoComponentDataFrame
  comment?: string
}> = ({ sectionStats, nominalThickness, relevantCDF, comment }) => {
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const { formatMeasurement } = useRugReportOps()

  const { userFacingThicknessUnit, userFacingXUnit, userFacingYUnit } =
    useMemo(() => {
      const thicknessUnit = relevantCDF?.data.params.measurement_unit
      const xUnit = relevantCDF?.data.geometry.x.unit
      const yUnit = relevantCDF?.data.geometry.y.unit

      return {
        userFacingThicknessUnit: thicknessUnit
          ? getPreferredMeasurementUnit(thicknessUnit)
          : undefined,
        userFacingXUnit: xUnit ? getPreferredMeasurementUnit(xUnit) : undefined,
        userFacingYUnit: yUnit ? getPreferredMeasurementUnit(yUnit) : undefined,
      }
    }, [getPreferredMeasurementUnit, relevantCDF])

  const { minThicknesses, averageThickness } = useMemo(() => {
    const stats = sectionStats ?? {
      minThicknesses: [],
      averageThickness: GeckoMeasurement.CreateFromValue(
        Number.NaN,
        userFacingThicknessUnit ?? GeckoMeasurementUnit.None
      ),
    }

    return {
      minThicknesses: sortBy(
        stats.minThicknesses.map((minThickness) => ({
          thickness: minThickness.thickness.convertTo(userFacingThicknessUnit),
          location:
            userFacingXUnit && userFacingYUnit
              ? minThickness.location.project(
                  GeckoComponentSpace.CreateFromMeasurementUnits(
                    minThickness.location.componentID,
                    userFacingXUnit,
                    userFacingYUnit
                  )
                )
              : minThickness.location,
        })),
        (thickness) =>
          thickness.thickness.toNumber(
            userFacingThicknessUnit ?? thickness.thickness.unit
          )
      ),
      averageThickness: stats.averageThickness.convertTo(
        userFacingThicknessUnit
      ),
    }
  }, [sectionStats, userFacingThicknessUnit, userFacingXUnit, userFacingYUnit])

  const sectionNominalThickness = nominalThickness?.convertTo(
    userFacingThicknessUnit
  )

  const maxWallLoss =
    sectionNominalThickness &&
    minThicknesses.length > 0 &&
    userFacingThicknessUnit
      ? (1 -
          Math.min(
            ...minThicknesses.map((minThickness) =>
              minThickness.thickness.toNumber(userFacingThicknessUnit)
            )
          ) /
            sectionNominalThickness.toNumber(userFacingThicknessUnit)) *
        MATH_CONSTANTS.PERCENTAGE_SCALE
      : undefined

  const maxWallLossMeasurement =
    maxWallLoss !== undefined
      ? GeckoMeasurement.CreateFromValue(
          maxWallLoss,
          GeckoMeasurementUnit.Percent
        )
      : undefined

  const maxWallLossDisplay =
    formatMeasurement({
      measurement: maxWallLossMeasurement,
      variant: 'narrow',
      minimumFractionDigits: MATH_CONSTANTS.FIXED_DECIMAL_PLACES,
      maximumFractionDigits: MATH_CONSTANTS.FIXED_DECIMAL_PLACES,
    }) ?? 'N/A'

  const nominalThicknessDisplay = formatMeasurement({
    measurement: sectionNominalThickness,
    variant: 'narrow',
  })

  const averageThicknessDisplay = formatMeasurement({
    measurement: averageThickness,
    variant: 'narrow',
  })

  const measurementRows = useMemo(
    () => [
      {
        label: 'Nominal Thickness',
        value: nominalThicknessDisplay,
        locationX: 'Location-X',
        locationY: 'Location-Y',
      },
      ...minThicknesses.map((minThickness, index) => ({
        label: `Minimum Thickness ${index + 1}`,
        value: formatMeasurement({
          measurement: minThickness.thickness,
          variant: 'narrow',
        }),
        locationX: formatLocation(
          formatMeasurement({
            measurement: minThickness.location.x,
            variant: 'geometric',
          })
        ),
        locationY: formatLocation(
          formatMeasurement({
            measurement: minThickness.location.y,
            variant: 'geometric',
          })
        ),
      })),
      {
        label: 'Max Wall Loss',
        value: maxWallLossDisplay,
        locationX: undefined,
        locationY: undefined,
      },
      {
        label: 'Average Thickness',
        value: averageThicknessDisplay,
        locationX: undefined,
        locationY: undefined,
      },
    ],
    [
      averageThicknessDisplay,
      formatMeasurement,
      maxWallLossDisplay,
      minThicknesses,
      nominalThicknessDisplay,
    ]
  )

  const columnWidths = useMemo<MeasurementColumnWidths>(() => {
    const labelWidth = estimatePdfColumnWidth(
      measurementRows.map((row) => row.label),
      118,
      170
    )
    const valueWidth = estimatePdfColumnWidth(
      measurementRows.map((row) => row.value),
      70,
      104
    )
    const locationXWidth = estimatePdfColumnWidth(
      measurementRows.map((row) => row.locationX).concat('Location-X'),
      76,
      104
    )
    const locationYWidth = estimatePdfColumnWidth(
      measurementRows.map((row) => row.locationY).concat('Location-Y'),
      68,
      96
    )

    return {
      label: labelWidth,
      value: valueWidth,
      locationX: locationXWidth,
      locationY: locationYWidth,
      total: labelWidth + valueWidth + locationXWidth + locationYWidth,
    }
  }, [measurementRows])

  const totalDataRows = measurementRows.length
  const commentText = comment?.trim()

  return (
    <View style={tableStyles.container}>
      <View style={tableStyles.tableRow}>
        <View
          style={[
            tableStyles.tableCell,
            {
              width: columnWidths.total,
              borderBottom: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
            },
          ]}>
          <Text
            style={[
              tableStyles.headerText,
              { color: REPORT_COLORS.GECKO_BLUE_GREEN },
            ]}>
            Measurements
          </Text>
        </View>
        <View
          style={[
            tableStyles.tableCell,
            tableStyles.tableCellLast,
            {
              flex: 1,
              borderBottom: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
            },
          ]}>
          <Text style={tableStyles.headerText}>Comments</Text>
        </View>
      </View>

      <View style={{ flexDirection: 'row' }}>
        <View style={{ width: columnWidths.total }}>
          {measurementRows.map((row, index) => (
            <TableRow
              key={`${row.label}-${index}`}
              columnWidths={columnWidths}
              label={row.label}
              value={row.value}
              locationX={row.locationX}
              locationY={row.locationY}
              isLastRow={index === measurementRows.length - 1}
            />
          ))}
        </View>
        <View
          style={{
            flex: 1,
            borderLeft: `${LAYOUT.BORDER_WIDTH.THIN} solid ${REPORT_COLORS.BORDER_GRAY}`,
            minHeight: totalDataRows * 20,
            padding: SPACING.PADDING.EXTRA_SMALL,
          }}>
          {commentText ? (
            <Text
              style={{
                fontSize: TYPOGRAPHY.FONT_SIZE.SMALL,
                color: REPORT_COLORS.DARKER_GRAY,
              }}>
              {commentText}
            </Text>
          ) : null}
        </View>
      </View>
    </View>
  )
}

const RenderReact: FC = () => {
  const {
    formData,
    handleAssetDrawingsFinish,
    selectedComponents,
    relevantCDFs,
    metadataEntryId,
    selectedComponentModels,
    assetScreenshots,
    sectionsByComponent,
    forceRefreshAssetScreenshots,
    unit,
    cdfCache,
    componentDefaults,
    componentDatasets,
    selectedAssetModel,
    inspections,
  } = useRugReportOps()

  const [selectedComponentId, setSelectedComponentId] = useState<
    ComponentId | undefined
  >()
  const [selectedCDFId, setSelectedCDFId] = useState<string | undefined>()
  const [capturingSection, setCapturingSection] = useState(false)
  const [currentSectionIdx, setCurrentSectionIdx] = useState<number | null>(
    null
  )
  const [captureTargetCount, setCaptureTargetCount] = useState(0)
  const [deletingImageKey, setDeletingImageKey] = useState<string | null>(null)
  const [editedComments, setEditedComments] = useState<Record<string, string>>(
    {}
  )

  const { rolodexUploadFile, rolodexDeleteFile } = useRolodexEdit()
  const { getPreferredMeasurementUnit } = useGeckoLocale()

  const selectedComponent = useMemo(() => {
    if (!selectedComponentId || !selectedComponents) return undefined
    return selectedComponents.find(
      (component) => component.id === selectedComponentId
    )
  }, [selectedComponentId, selectedComponents])

  const { sections, sectionable } = useMemo(() => {
    if (!selectedComponent) {
      return {
        sections: [] as Section[],
        sectionable: undefined,
      }
    }

    const componentSections = sectionsByComponent.get(
      selectedComponent.id as ComponentId
    )

    return {
      sections: componentSections?.sections ?? [],
      sectionable: componentSections,
    }
  }, [selectedComponent, sectionsByComponent])

  const hasSections = sections.length > 0

  const componentCDFs = useSelectableComponentCDFs({
    selectedComponent,
    relevantCDFs,
    componentDatasets,
    selectedAssetModel,
    inspections,
  })

  const relevantCDF = useMemo(() => {
    if (!selectedComponent || componentCDFs.length === 0) return undefined
    if (selectedCDFId) {
      return componentCDFs.find((cdf) => cdf.id === selectedCDFId)
    }
    return componentCDFs[0]
  }, [componentCDFs, selectedCDFId, selectedComponent])

  useEffect(() => {
    if (selectedComponent && componentCDFs.length > 0) {
      if (
        !selectedCDFId ||
        !componentCDFs.find((cdf) => cdf.id === selectedCDFId)
      ) {
        setSelectedCDFId(componentCDFs[0].id)
      }
      return
    }

    setSelectedCDFId(undefined)
  }, [componentCDFs, selectedCDFId, selectedComponent])

  const colorThreshold = useMemo(() => {
    if (!relevantCDF) return undefined

    const selectedComponentDefault = componentDefaults.find(
      (componentDefault) =>
        componentDefault.tags.component === selectedComponent?.id
    )

    return selectedComponentDefault?.data.color_scale_thresholds.get(
      relevantCDF.data.params.view.id
    )
  }, [componentDefaults, relevantCDF, selectedComponent?.id])

  const relevantCdfLegendTitle = useMemo(() => {
    if (!relevantCDF) return undefined

    return getCdfDisplayMetadata(
      relevantCDF,
      getPreferredMeasurementUnit(relevantCDF.data.params.measurement_unit)
    ).label
  }, [getPreferredMeasurementUnit, relevantCDF])

  const existingScreenshots = useMemo(() => {
    const screenshots: SectionScreenshot[] = []

    assetScreenshots?.sectionHeatmap?.forEach((screenshot) => {
      if (!screenshot.signedUrl) return

      const component = selectedComponents?.find(
        (candidate) => candidate.id === screenshot.componentId
      )
      if (!component) return

      const componentSections = sectionsByComponent.get(
        screenshot.componentId as ComponentId
      )
      const section = findSectionByKey(componentSections, screenshot.sectionKey)
      const sortOrder = section
        ? (componentSections?.sections.findIndex(
            (candidate) => candidate.name === section.name
          ) ?? -1)
        : -1

      screenshots.push({
        componentId: screenshot.componentId,
        componentName: component.data.name,
        sectionKey:
          screenshot.sectionKey ?? createReportHeatmapSectionKey(undefined),
        sectionName:
          section?.name ??
          (isFullComponentSectionKey(screenshot.sectionKey)
            ? component.data.name
            : (screenshot.sectionKey ?? component.data.name)),
        sortOrder,
        signedUrl: screenshot.signedUrl,
        heatmapPath: screenshot.heatmapPath,
        imagePath: screenshot.imagePath,
        legendSignedUrl: screenshot.legendSignedUrl,
        legendPath: screenshot.legendPath,
        descriptorPath: screenshot.descriptorPath,
        bundlePath: screenshot.bundlePath,
      })
    })

    return screenshots
  }, [assetScreenshots, sectionsByComponent, selectedComponents])

  useEffect(() => {
    setEditedComments((currentComments) => {
      const nextComments = { ...currentComments }

      existingScreenshots.forEach((screenshot) => {
        const commentKey = getSectionHeatmapCommentKey(
          screenshot.componentId,
          screenshot.sectionKey
        )
        nextComments[commentKey] =
          formData.assetDrawings.images[commentKey]?.imageCaption ?? ''
      })

      return nextComments
    })
  }, [existingScreenshots, formData.assetDrawings.images])

  const handleDeleteScreenshot = useCallback(
    async (screenshot: SectionScreenshot) => {
      const screenshotKey = `${screenshot.componentId}-${screenshot.sectionKey}`
      setDeletingImageKey(screenshotKey)
      try {
        for (const path of getBundleFilePaths(screenshot)) {
          await rolodexDeleteFile(metadataEntryId, path)
        }
        message.success('Heatmap deleted successfully')
        await forceRefreshAssetScreenshots()
      } catch (error) {
        console.error('Failed to delete section heatmap bundle:', error)
        message.error('Failed to delete heatmap')
      } finally {
        setDeletingImageKey(null)
      }
    },
    [forceRefreshAssetScreenshots, metadataEntryId, rolodexDeleteFile]
  )

  const handleCommentChange = useCallback(
    (commentKey: string, value: string) => {
      setEditedComments((currentComments) => ({
        ...currentComments,
        [commentKey]: value,
      }))
    },
    []
  )

  const handleCommentSave = useCallback(
    async (screenshot: SectionScreenshot) => {
      const commentKey = getSectionHeatmapCommentKey(
        screenshot.componentId,
        screenshot.sectionKey
      )
      await handleAssetDrawingsFinish(
        screenshot.imagePath,
        commentKey,
        screenshot.sortOrder,
        editedComments[commentKey]?.trim() || undefined
      )
    },
    [editedComments, handleAssetDrawingsFinish]
  )

  const cdfDataFetcher = useCdfDataFetcher()
  const startCapture = useCallback(async () => {
    if (!selectedComponent || !relevantCDF) {
      message.error('Unable to generate heatmaps - missing required data')
      return
    }

    const selectedComponentModel = selectedComponentModels?.find(
      (componentModel) => componentModel.id === selectedComponent.id
    )

    if (!selectedComponentModel) {
      message.error('Unable to generate heatmaps - missing component model')
      return
    }

    setCapturingSection(true)

    try {
      const buffer = await cdfDataFetcher(relevantCDF)
      if (!buffer) {
        message.error('Unable to generate heatmaps - missing CDF data')
        return
      }

      const captureTargets = hasSections
        ? getBoundsFromSectionable(
            selectedComponent.id as ComponentId,
            sectionable!,
            relevantCDF.data.geometry
          ).map((bounds, index) => ({
            sectionKey: createReportHeatmapSectionKey(sections[index]?.name),
            bounds,
          }))
        : [
            {
              sectionKey: createReportHeatmapSectionKey(undefined),
              bounds: selectedComponentModel.componentBounds,
            },
          ]

      setCaptureTargetCount(captureTargets.length)

      const existingBundles =
        assetScreenshots?.sectionHeatmap?.filter(
          (screenshot) => screenshot.componentId === selectedComponent.id
        ) ?? []
      for (const existingBundle of existingBundles) {
        for (const path of getBundleFilePaths(existingBundle)) {
          await rolodexDeleteFile(metadataEntryId, path)
        }
      }

      for (const [index, target] of captureTargets.entries()) {
        setCurrentSectionIdx(index)

        const bundle = await renderReportHeatmapBundle({
          kind: 'section',
          componentId: selectedComponent.id as ComponentId,
          sectionKey: target.sectionKey,
          cdfId: relevantCDF.id,
          legendTitle: relevantCdfLegendTitle,
          cdf: relevantCDF,
          componentModel: selectedComponentModel,
          bounds: target.bounds,
          colorThreshold,
          includeAxes: true,
          includeLegend: true,
          includeOverlay: false,
          overlayMode: ReportOverlayMode.None,
          labelMode: ReportLabelMode.None,
          outputWidthPx: 2000,
          background: 'white',
          rawValues: buffer.values,
        })

        const paths = buildReportHeatmapBundlePaths({
          kind: 'section',
          componentId: selectedComponent.id as ComponentId,
          sectionKey: target.sectionKey,
          descriptorHash: bundle.descriptor.hash,
        })

        await Promise.all([
          rolodexUploadFile(
            metadataEntryId,
            bundle.heatmapBlob,
            paths.heatmapPath
          ),
          rolodexUploadFile(
            metadataEntryId,
            bundle.legendBlob,
            paths.legendPath
          ),
          rolodexUploadFile(
            metadataEntryId,
            new Blob([serializeReportHeatmapDescriptor(bundle.descriptor)], {
              type: 'application/json',
            }),
            paths.descriptorPath
          ),
        ])
      }

      message.success('Heatmaps generated successfully')
      await forceRefreshAssetScreenshots()
    } catch (error) {
      console.error('Failed to generate section heatmaps:', error)
      message.error('Failed to generate heatmaps')
    } finally {
      setCapturingSection(false)
      setCurrentSectionIdx(null)
      setCaptureTargetCount(0)
    }
  }, [
    assetScreenshots?.sectionHeatmap,
    cdfDataFetcher,
    colorThreshold,
    forceRefreshAssetScreenshots,
    hasSections,
    relevantCdfLegendTitle,
    metadataEntryId,
    relevantCDF,
    rolodexDeleteFile,
    rolodexUploadFile,
    sectionable,
    sections,
    selectedComponent,
    selectedComponentModels,
  ])

  const componentOptions = useMemo(() => {
    if (!selectedComponents) return []
    return selectedComponents.map((component) => ({
      label: component.data.name,
      value: component.id,
    }))
  }, [selectedComponents])

  useInitializeDerivedCdfsFromRolodexData(
    cdfCache,
    CommercialFdeCdfSpecificationList,
    unit?.id ?? [],
    isInspectionThicknessCDF
  )

  const cdfOptions = componentCDFs.map((cdf) => ({
    label: `${
      getCdfDisplayMetadata(
        cdf,
        getPreferredMeasurementUnit(cdf.data.params.measurement_unit)
      ).label
    }${
      cdf.data.params.aggregation.name
        ? ` (${cdf.data.params.aggregation.name})`
        : ''
    }`,
    value: cdf.id,
  }))

  const screenshotsByComponent = useMemo(() => {
    const grouped = new Map<string, SectionScreenshot[]>()

    existingScreenshots.forEach((screenshot) => {
      const existing = grouped.get(screenshot.componentId) ?? []
      existing.push(screenshot)
      grouped.set(screenshot.componentId, existing)
    })

    grouped.forEach((screenshots) => {
      screenshots.sort((left, right) => left.sortOrder - right.sortOrder)
    })

    return grouped
  }, [existingScreenshots])

  return (
    <Container>
      <ComponentSelector>
        <Space orientation='vertical' style={{ width: '100%' }}>
          <AntText strong>Select Component:</AntText>
          <Select
            style={{ width: '100%' }}
            placeholder='Select a component'
            value={selectedComponentId}
            onChange={setSelectedComponentId}
            options={componentOptions}
          />
          {selectedComponent && cdfOptions.length > 0 && (
            <>
              <AntText strong>Select CDF:</AntText>
              <Select
                style={{ width: '100%' }}
                placeholder='Select a CDF'
                value={selectedCDFId}
                onChange={setSelectedCDFId}
                options={cdfOptions}
              />
            </>
          )}
          {componentOptions.length === 0 && (
            <Alert message='No components available' type='warning' showIcon />
          )}
        </Space>
      </ComponentSelector>

      {selectedComponent && (
        <Space direction='vertical' size='small'>
          <Button
            type='primary'
            onClick={startCapture}
            loading={capturingSection}
            disabled={!selectedComponent}>
            {capturingSection ? 'Generating Heatmaps...' : 'Generate Heatmaps'}
          </Button>
          {capturingSection && currentSectionIdx !== null && (
            <AntText type='secondary'>
              Rendering {currentSectionIdx + 1} of {captureTargetCount}
            </AntText>
          )}
        </Space>
      )}

      <PreviewSection>
        <Typography.Title level={4}>Captured Section Heatmaps</Typography.Title>
        {Array.from(screenshotsByComponent.entries()).map(
          ([componentId, screenshots]) => {
            const component = selectedComponents?.find(
              (candidate) => candidate.id === componentId
            )
            return (
              <div key={componentId}>
                <Typography.Title level={5}>
                  {component?.data.name}
                </Typography.Title>
                {screenshots.map((screenshot) => {
                  const screenshotKey = `${screenshot.componentId}-${screenshot.sectionKey}`
                  return (
                    <ImageCard
                      key={screenshotKey}
                      title={screenshot.sectionName}>
                      <div
                        style={{
                          display: 'flex',
                          gap: '12px',
                          alignItems: 'flex-start',
                          width: '100%',
                        }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <AntImage
                            src={screenshot.signedUrl}
                            alt={`${screenshot.componentName} - ${screenshot.sectionName}`}
                            style={{
                              display: 'block',
                              width: '100%',
                            }}
                          />
                        </div>
                        {screenshot.legendSignedUrl && (
                          <AntImage
                            src={screenshot.legendSignedUrl}
                            alt={`${screenshot.sectionName} legend`}
                            style={{ width: '72px' }}
                          />
                        )}
                      </div>
                      <div style={{ marginTop: '0.75rem' }}>
                        <AntText strong>Comments</AntText>
                        <Input.TextArea
                          rows={3}
                          placeholder='Add comments for this section heatmap'
                          value={
                            editedComments[
                              getSectionHeatmapCommentKey(
                                screenshot.componentId,
                                screenshot.sectionKey
                              )
                            ] ?? ''
                          }
                          onChange={(event) =>
                            handleCommentChange(
                              getSectionHeatmapCommentKey(
                                screenshot.componentId,
                                screenshot.sectionKey
                              ),
                              event.target.value
                            )
                          }
                          style={{ marginTop: '0.5rem' }}
                        />
                        <Button
                          onClick={() => handleCommentSave(screenshot)}
                          style={{ marginTop: '0.5rem' }}>
                          Save Comments
                        </Button>
                      </div>
                      <Popconfirm
                        title='Delete heatmap?'
                        description='This will remove the heatmap bundle from the report.'
                        okText='Delete'
                        cancelText='Cancel'
                        okButtonProps={{
                          danger: true,
                          loading: deletingImageKey === screenshotKey,
                        }}
                        onConfirm={() => handleDeleteScreenshot(screenshot)}>
                        <Button
                          danger
                          loading={deletingImageKey === screenshotKey}
                          style={{ marginTop: '0.75rem' }}>
                          Delete Heatmap
                        </Button>
                      </Popconfirm>
                    </ImageCard>
                  )
                })}
              </div>
            )
          }
        )}
      </PreviewSection>
    </Container>
  )
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const {
    formData,
    selectedComponents,
    assetScreenshots,
    sectionsByComponent,
    statsBySection,
    componentToNominalRange,
    relevantCDFs,
  } = useRugReportOps()

  const screenshots = useMemo(() => {
    const shots: SectionScreenshot[] = []

    assetScreenshots?.sectionHeatmap?.forEach((screenshot) => {
      if (!screenshot.signedUrl) return

      const component = selectedComponents?.find(
        (candidate) => candidate.id === screenshot.componentId
      )
      if (!component) return

      const componentSections = sectionsByComponent.get(
        screenshot.componentId as ComponentId
      )
      const section = findSectionByKey(componentSections, screenshot.sectionKey)
      const sortOrder = section
        ? (componentSections?.sections.findIndex(
            (candidate) => candidate.name === section.name
          ) ?? -1)
        : -1

      shots.push({
        componentId: screenshot.componentId,
        componentName: component.data.name,
        sectionKey:
          screenshot.sectionKey ?? createReportHeatmapSectionKey(undefined),
        sectionName:
          section?.name ??
          (isFullComponentSectionKey(screenshot.sectionKey)
            ? component.data.name
            : (screenshot.sectionKey ?? component.data.name)),
        sortOrder,
        signedUrl: screenshot.signedUrl,
        heatmapPath: screenshot.heatmapPath,
        imagePath: screenshot.imagePath,
        legendSignedUrl: screenshot.legendSignedUrl,
        legendPath: screenshot.legendPath,
        descriptorPath: screenshot.descriptorPath,
        bundlePath: screenshot.bundlePath,
      })
    })

    return sortBy(shots, ['componentId', 'sortOrder', 'sectionName'])
  }, [assetScreenshots, sectionsByComponent, selectedComponents])

  if (screenshots.length === 0) return null

  return (
    <>
      {screenshots.map((screenshot, index) => {
        const tracker =
          index === 0
            ? {
                sectionId: RugReportSectionId.RUG_DATA_IMAGES,
                onRender,
              }
            : undefined

        return (
          <ReportPage
            key={`${screenshot.componentId}-${screenshot.sectionKey}`}
            id={index === 0 ? 'rug-data-images' : undefined}
            size='LETTER'
            orientation='landscape'
            pageStyle={pageStyles.imagePage}
            contentBottomPadding={20}
            tracker={tracker}
            header={<PageHeader title='RUG DATA IMAGES' />}
            footer={
              <PageFooter
                jobNumber={formData.mainInfo.assetId}
                revisionNumber={formData.revisionMetadata.revisionNumber}
              />
            }>
            <ImagePageHeader
              componentId={screenshot.componentId as ComponentId}
            />

            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'flex-start',
                marginBottom: 2,
                gap: 16,
              }}>
              <Text
                style={{
                  fontSize: TYPOGRAPHY.FONT_SIZE.SMALL,
                  color: REPORT_COLORS.GECKO_BLUE_GREEN,
                  fontWeight: 'bold',
                }}>
                {screenshot.sectionName}
              </Text>
            </View>

            {(() => {
              const componentStats = statsBySection?.get(
                screenshot.componentId as ComponentId
              )
              const sectionStats = componentStats?.get(screenshot.sectionName)
              const nominalRange = componentToNominalRange?.get(
                screenshot.componentId as ComponentId
              )
              const fallbackNominalThickness =
                nominalRange &&
                GeckoMeasurement.Equivalent(nominalRange.min, nominalRange.max)
                  ? nominalRange.min
                  : undefined
              const sectionNominalThickness =
                sectionStats?.nominalThickness ?? fallbackNominalThickness
              const relevantCDF = relevantCDFs?.find(
                (cdf) =>
                  cdf.data.geometry.componentID === screenshot.componentId
              )
              const sectionable = sectionsByComponent.get(
                screenshot.componentId as ComponentId
              )
              const section = findSectionByKey(
                sectionable,
                screenshot.sectionKey
              )
              const comment =
                formData.assetDrawings.images[
                  getSectionHeatmapCommentKey(
                    screenshot.componentId,
                    screenshot.sectionKey
                  )
                ]?.imageCaption

              return (
                <>
                  <View
                    style={{
                      marginBottom: 2,
                    }}>
                    <PdfHeatmapWithLegend
                      heatmapSrc={screenshot.signedUrl}
                      legendSrc={screenshot.legendSignedUrl}
                    />
                  </View>
                  {section && (
                    <SectionStatsTable
                      section={section}
                      sectionStats={sectionStats}
                      nominalThickness={sectionNominalThickness}
                      relevantCDF={relevantCDF}
                      comment={comment}
                    />
                  )}
                </>
              )
            })()}
          </ReportPage>
        )
      })}
      <AddendumPages
        sectionId={RugReportSectionId.RUG_DATA_IMAGES}
        sectionTitle='RUG Data Images'
      />
    </>
  )
}

export const RugDataImagesSection: ReportSection = {
  id: RugReportSectionId.RUG_DATA_IMAGES,
  title: 'RUG Data Images',
  description: 'Section-by-section thickness visualizations',
  required: true,
  RenderReact,
  RenderPDF,
  requiredFields: [],
}
